import { useState, useMemo, useCallback, useRef } from "react";
import DraggableModal from "../DraggableModal";
import EmailDialog from "../EmailDialog";
import { htmlToPdfBlob, safeFilename } from "../../pdfRenderer";
import { escHtml, reportStyles, policyFooter, openPreviewWindow, downloadBlob, fmtDateShort } from "./auditCommon";

/* ----------------------------------------------------------------------------
 * Audit Summary report — a one-document index of everything done in the audit:
 * audit header data, the list of generated reports (with date/time), and the
 * list of uploaded documents. Generated as a PDF and stored in the audit like
 * any other report. Uses reportStyles() (Arial) so text renders with proper
 * spacing under html2canvas.
 * -------------------------------------------------------------------------- */

const T = {
  en: {
    title: "Audit Summary Report",
    close: "Close", preview: "Preview", pdf: "Download PDF", email: "Email", busy: "Working…",
    docTitle: "AUDIT SUMMARY REPORT",
    auditName: "Audit", status: "Status", auditor: "Auditor", period: "Period",
    statusOpen: "Open", statusClosed: "Closed",
    generatedReports: "Generated Reports", uploadedDocs: "Uploaded Documents",
    colReport: "Report", colDate: "Date / Time", colRecipients: "Emailed To",
    colDoc: "Document", colFile: "File", colSize: "Size",
    noReports: "No reports generated.", noDocs: "No documents uploaded.",
    totalReports: "Total reports", totalDocs: "Total documents",
    generatedOn: "Summary generated on",
    emailSubject: "Audit Summary Report",
    emailBody: "Attached is the Audit Summary Report for audit ",
  },
  es: {
    title: "Reporte Resumen de Auditoría",
    close: "Cerrar", preview: "Vista previa", pdf: "Descargar PDF", email: "Correo", busy: "Trabajando…",
    docTitle: "REPORTE RESUMEN DE AUDITORÍA",
    auditName: "Auditoría", status: "Estado", auditor: "Auditor", period: "Período",
    statusOpen: "Abierta", statusClosed: "Cerrada",
    generatedReports: "Reportes Generados", uploadedDocs: "Documentos Cargados",
    colReport: "Reporte", colDate: "Fecha / Hora", colRecipients: "Enviado a",
    colDoc: "Documento", colFile: "Archivo", colSize: "Tamaño",
    noReports: "No se generaron reportes.", noDocs: "No se cargaron documentos.",
    totalReports: "Total de reportes", totalDocs: "Total de documentos",
    generatedOn: "Resumen generado el",
    emailSubject: "Reporte Resumen de Auditoría",
    emailBody: "Adjunto el Reporte Resumen de Auditoría para la auditoría ",
  },
};

/* Human-readable report-type labels (mirrors the registry's report cards). */
const TYPE_LABELS = {
  en: {
    mass_balance: "Mass Balance", organic_certs: "Organic Certificates",
    traceability: "Traceability", organic_labels: "Organic Labels",
    general_info: "General Information & Flow Diagram",
    traceability_proc: "Traceability Procedure",
    complaints_proc: "Complaint Reception & Handling Program",
    audit_summary: "Audit Summary Report",
  },
  es: {
    mass_balance: "Balance de Masa", organic_certs: "Certificados Orgánicos",
    traceability: "Trazabilidad", organic_labels: "Etiquetas Orgánicas",
    general_info: "Información General y Diagrama de Flujo",
    traceability_proc: "Procedimiento de Trazabilidad",
    complaints_proc: "Programa de Recepción y Manejo de Quejas",
    audit_summary: "Reporte Resumen de Auditoría",
  },
};

function fmtDateTime(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(lang === "es" ? "es-MX" : "en-US", {
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch (e) { return String(iso); }
}

function fmtSize(bytes) {
  var n = Number(bytes || 0);
  if (!n) return "—";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + " KB";
  return (n / (1024 * 1024)).toFixed(1) + " MB";
}

function statusLabel(status, t) {
  var s = String(status || "open").toLowerCase();
  return (s === "closed" || s === "completed") ? t.statusClosed : t.statusOpen;
}

function buildHtml(audit, lang) {
  const t = T[lang === "es" ? "es" : "en"];
  const labels = TYPE_LABELS[lang === "es" ? "es" : "en"];
  const reports = Array.isArray(audit && audit.reports) ? audit.reports : [];
  const docs = Array.isArray(audit && audit.otherDocs) ? audit.otherDocs : [];

  var periodStr = "";
  if (audit && audit.startDate) periodStr += fmtDateShort(audit.startDate);
  if (audit && audit.endDate) periodStr += " — " + fmtDateShort(audit.endDate);
  if (!periodStr && audit && audit.year) periodStr = String(audit.year);

  /* Header info block. */
  var info = '<table class="info-table"><tbody>'
    + '<tr><td class="k">' + escHtml(t.auditName) + '</td><td>' + escHtml(audit && audit.name || "—") + '</td>'
    + '<td class="k">' + escHtml(t.status) + '</td><td>' + escHtml(statusLabel(audit && audit.status, t)) + '</td></tr>'
    + '<tr><td class="k">' + escHtml(t.auditor) + '</td><td>' + escHtml(audit && audit.auditorName || "—") + '</td>'
    + '<td class="k">' + escHtml(t.period) + '</td><td>' + escHtml(periodStr || "—") + '</td></tr>'
    + '</tbody></table>';

  /* Reports table. */
  var repRows = reports.map(function (r) {
    var label = (r && r.title) || labels[r && r.type] || (r && r.type) || "—";
    var rec = Array.isArray(r && r.recipients) && r.recipients.length
      ? r.recipients.map(function (x) { return escHtml(x && (x.email || x.name) || ""); }).filter(Boolean).join(", ")
      : "—";
    return '<tr><td>' + escHtml(label) + '</td><td class="mono">' + escHtml(fmtDateTime(r && r.generatedAt, lang)) + '</td><td>' + rec + '</td></tr>';
  }).join("");
  if (!repRows) repRows = '<tr><td colspan="3" class="empty">' + escHtml(t.noReports) + '</td></tr>';

  /* Docs table. */
  var docRows = docs.map(function (d) {
    return '<tr><td>' + escHtml(d && d.name || "—") + '</td><td>' + escHtml(d && d.fileName || "—")
      + '</td><td class="mono">' + escHtml(fmtSize(d && d.fileSize)) + '</td></tr>';
  }).join("");
  if (!docRows) docRows = '<tr><td colspan="3" class="empty">' + escHtml(t.noDocs) + '</td></tr>';

  var nowStr = new Date().toLocaleString(lang === "es" ? "es-MX" : "en-US");

  var extraCss = ''
    + '.page{max-width:780px;margin:0 auto;padding:6px 0}'
    + '.summary-title{text-align:center;font-size:15px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#1B3A4B;margin:4px 0 16px}'
    + '.info-table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:18px}'
    + '.info-table td{border:1px solid #d1d5db;padding:6px 9px}'
    + '.info-table td.k{background:#f3f4f6;font-weight:700;color:#1B3A4B;text-transform:uppercase;letter-spacing:.04em;font-size:9px;width:16%}'
    + '.sec-title{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#1B3A4B;border-bottom:2px solid #1B3A4B;padding-bottom:3px;margin:18px 0 8px}'
    + '.list-table{width:100%;border-collapse:collapse;font-size:10.5px;margin-bottom:6px}'
    + '.list-table th{background:#f3f4f6;text-align:left;padding:6px 8px;font-weight:700;font-size:9px;text-transform:uppercase;letter-spacing:.04em;color:#444;border-bottom:1.5px solid #d1d5db}'
    + '.list-table td{padding:6px 8px;border-bottom:1px solid #e5e7eb;vertical-align:top}'
    + '.list-table td.mono{font-family:"Courier New",monospace;white-space:nowrap}'
    + '.list-table td.empty{text-align:center;color:#888;font-style:italic;padding:12px}'
    + '.count{font-size:10px;color:#666;margin:2px 0 0;text-align:right}'
    + '.gen-on{font-size:9px;color:#888;text-align:center;margin-top:18px}';

  return '<!doctype html><html><head><meta charset="utf-8"><style>' + reportStyles() + extraCss + '</style>' +
    '</head><body><div class="page">' +
    '<div class="summary-title">' + escHtml(t.docTitle) + '</div>' +
    info +
    '<div class="sec-title">' + escHtml(t.generatedReports) + '</div>' +
    '<table class="list-table"><thead><tr><th>' + escHtml(t.colReport) + '</th><th>' + escHtml(t.colDate) + '</th><th>' + escHtml(t.colRecipients) + '</th></tr></thead><tbody>' +
    repRows + '</tbody></table>' +
    '<div class="count">' + escHtml(t.totalReports) + ': ' + reports.length + '</div>' +
    '<div class="sec-title">' + escHtml(t.uploadedDocs) + '</div>' +
    '<table class="list-table"><thead><tr><th>' + escHtml(t.colDoc) + '</th><th>' + escHtml(t.colFile) + '</th><th>' + escHtml(t.colSize) + '</th></tr></thead><tbody>' +
    docRows + '</tbody></table>' +
    '<div class="count">' + escHtml(t.totalDocs) + ': ' + docs.length + '</div>' +
    '<div class="gen-on">' + escHtml(t.generatedOn) + ' ' + escHtml(nowStr) + '</div>' +
    policyFooter() +
    '</div></body></html>';
}

export default function AuditSummaryPanel({ lang, audit, onClose, onLogReport, initialParams }) {
  const t = T[lang === "es" ? "es" : "en"];
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const lastBlobRef = useRef(null);
  const lastFilenameRef = useRef("");

  const makeHtml = useCallback(() => buildHtml(audit, lang), [audit, lang]);
  const previewHtml = useMemo(() => makeHtml(), [makeHtml]);

  const handlePreview = () => openPreviewWindow(makeHtml());
  const fileBase = "Audit_Summary_" + (audit && audit.name || "");

  const handlePdf = async () => {
    setBusy(true);
    try {
      const blob = await htmlToPdfBlob(makeHtml(), { orientation: "portrait" });
      const fn = safeFilename(fileBase) + ".pdf";
      lastBlobRef.current = blob; lastFilenameRef.current = fn;
      downloadBlob(blob, fn);
      if (onLogReport) onLogReport({ type: "audit_summary", title: t.title, params: {}, blob: blob, filename: fn, mime: "application/pdf" });
    } finally { setBusy(false); }
  };

  const profile = {};
  const btnSecondary = { padding: "7px 14px", border: "1px solid var(--color-border)", borderRadius: 6, background: "#fff", fontSize: 12, cursor: "pointer" };
  const btnPrimary = { padding: "7px 14px", border: "none", borderRadius: 6, background: "#1a2636", color: "#fff", fontSize: 12, cursor: "pointer" };

  return (
    <DraggableModal title={t.title + " · " + (audit && audit.name || "")} onClose={onClose} zIndex={10003}>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        <div style={{ border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden", background: "#fff", height: 460 }}>
          <iframe title="audit-summary-preview" srcDoc={previewHtml} style={{ width: "100%", height: "100%", border: 0 }} />
        </div>
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{busy ? t.busy : ""}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={btnSecondary}>{t.close}</button>
          <button onClick={handlePreview} style={btnSecondary}>{t.preview}</button>
          <button onClick={handlePdf} style={btnPrimary} disabled={busy}>{t.pdf}</button>
        </div>
      </div>
    </DraggableModal>
  );
}
